/** @jsxImportSource @emotion/react */
import ZoomIn from '@mui/icons-material/ZoomIn'
import { Box, IconButton, Modal, useTheme } from '@mui/material'
import React from 'react'
import {
  thumbnailContainerStyle,
  thumbnailImageStyle,
  hoverOverlayStyle,
  modalContainerStyle,
  modalImageStyle,
  modalContentStyle,
  closeButtonStyle,
} from './ArticleImageBlock.style'
import { GridCloseIcon } from '@mui/x-data-grid'

export interface ArticleImageBlockProps extends React.ComponentPropsWithoutRef<'img'> {
  src?: string
  alt?: string
  title?: string
}

const ArticleImageBlock: React.FC<ArticleImageBlockProps> = ({ src, alt, title, ...imgProps }) => {
  const theme = useTheme()
  const [open, setOpen] = React.useState(false)
  const [aspectRatio, setAspectRatio] = React.useState<number | undefined>(undefined)

  if (!src) return null

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement
    setAspectRatio(img.naturalWidth / img.naturalHeight)
  }

  const openLightbox = () => setOpen(true)
  const closeLightbox = () => setOpen(false)

  const onKeyDownThumb: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      openLightbox()
    }
  }

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    closeLightbox()
  }

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-label={alt || 'Open image'}
        onClick={openLightbox}
        onKeyDown={onKeyDownThumb}
        css={thumbnailContainerStyle(theme, aspectRatio)}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          title={title}
          onLoad={handleImageLoad}
          css={thumbnailImageStyle}
          {...imgProps}
        />

        <Box aria-hidden css={hoverOverlayStyle(theme)}>
          <ZoomIn fontSize="large" sx={{ color: theme.palette.common.white, opacity: 0.9 }} />
        </Box>
      </Box>

      <Modal
        open={open}
        onClose={closeLightbox}
        aria-labelledby="article-image-lightbox"
        aria-describedby="article-image-lightbox"
        slotProps={{
          backdrop: {
            style: { backgroundColor: 'rgba(0,0,0,0.8)' },
          },
        }}
        keepMounted
        closeAfterTransition
      >
        <Box onClick={closeLightbox} role="dialog" aria-modal="true" css={modalContainerStyle}>
          {/* White safe area container for images with no white bg */}
          <Box onClick={(e) => e.stopPropagation()} css={modalContentStyle(theme)}>
            <IconButton
              onClick={handleCloseClick}
              css={closeButtonStyle(theme)}
              aria-label="Close image"
              size="small"
            >
              <GridCloseIcon />
            </IconButton>

            <Box component="img" src={src} alt={alt} title={title} css={modalImageStyle(theme)} />
          </Box>
        </Box>
      </Modal>
    </>
  )
}

export default ArticleImageBlock
